import React, { useRef } from "react";
import { AgGridReact } from "ag-grid-react";
import { ColDef } from "ag-grid-community";
import { DynamicCellRenderer, OptionItem } from "./DynamicCellRenderer";
import { NameDescriptionCellRenderer } from "./NameDescriptionCellRenderer";

// ---------------------------------------
// Shared row type
// ---------------------------------------
export interface SettingsGridRow {
  id: string;
  label: string;
  description?: string;
  value?: boolean | string | number;
  type?: "boolean" | "select" | "text" | "timespan";
  options?: OptionItem[];
  isSection: boolean;
  disabled?: boolean;
  range?: {
    start: string;
    end: string;
    step: number;
    displayHHmm?: boolean;
    unit?: "seconds" | "minutes";
  };
}

// ---------------------------------------
// Props
// ---------------------------------------
interface GenericSettingsGridProps {
  rowData: SettingsGridRow[];
  onRowDataChange: (rowData: SettingsGridRow[]) => void;
  onSave: () => void;
  isDirty: boolean;
}

// ---------------------------------------
// GenericSettingsGrid Component
// ---------------------------------------
export const GenericSettingsGrid: React.FC<GenericSettingsGridProps> = ({
  rowData,
  onRowDataChange,
  onSave,
  isDirty,
}) => {
  const gridRef = useRef<AgGridReact>(null);

  const columns: ColDef<SettingsGridRow>[] = [
    {
      field: "label",
      flex: 1,
      autoHeight: true,
      wrapText: true,
      cellRenderer: NameDescriptionCellRenderer,
      cellStyle: (params) => ({
        padding: "8px 12px",
        paddingLeft: params.data?.isSection ? 12 : 36,
        fontWeight: params.data?.isSection ? 600 : 400,
      }),
    },
    {
      field: "value",
      flex: 1,
      editable: (params) => !params.data?.isSection && !params.data?.disabled && params.data?.type !== "text",
      cellRenderer: DynamicCellRenderer,
      cellStyle: {
        padding: "8px",
        display: "flex",
        alignItems: "center",
      },
    },
  ];

  return (
    <div style={{ width: "100%" }}>
      <div className="mb-3">
        <button
          className={`px-4 py-2 rounded text-white ${
            isDirty
              ? "bg-blue-600 hover:bg-blue-700"
              : "bg-gray-400 cursor-not-allowed"
          }`}
          onClick={onSave}
          disabled={!isDirty}
        >
          Save Changes
        </button>
      </div>

      <div className="ag-theme-alpine" style={{ width: "100%" }}>
        <AgGridReact
          ref={gridRef}
          headerHeight={0}
          domLayout="autoHeight"
          animateRows={false}
          rowData={rowData}
          columnDefs={columns}
          defaultColDef={{ resizable: false }}
          getRowId={(params) => params.data.id} /* Provide a unique ID for each row to prevent unnecessary re-renders */
          onCellValueChanged={(params) => {
            const updated = rowData.map(row =>
              row.id === params.data.id ? { ...row, value: params.newValue } : row
            );
            onRowDataChange(updated);
          }}
          getRowStyle={(params) => ({
            backgroundColor: params.data?.isSection ? "#f5f7fa" : "transparent",
            borderTop: params.data?.isSection ? "1px solid #dcdfe3" : "none",
            borderBottom: "1px solid #eee",
          })}
        />
      </div>
    </div>
  );
};

export default GenericSettingsGrid;
